/* eslint-disable max-len */
/* eslint-disable no-console */
import React, { FC, useState, ChangeEvent, useEffect, useRef, MouseEvent, KeyboardEvent } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import ReactTooltip from 'react-tooltip';

import { uploadImageToBackend } from './helpers/uploadImage';
import * as contentLibraryAction from '../../../../../actions/contentLibrary';
import * as Styled from './styles';
import handleKeyPress from '../../../../../common/handleKeyPress';
import eventService from '../../../../../common/eventService';
import BananatagButton from '../../../../../componentLibrary/BananatagButton';
import { IImagePreviewColumnProps } from '../../../../../types/imageLibrary';
import { ERR_RENAME_FILE, ERR_FILE_NAME_TOO_LONG } from '../../../../../constants/Errors';
import { request } from '../../../../../common/request';
import configs from '../../../../../constants/configs';

const MAX_FILE_CHAR_DISPLAY = 20;
const MAX_FILE_NAME_CHAR = 50;

const ImagePreviewColumn: FC<IImagePreviewColumnProps> = ({
    content,
    hideImageLibrary,
    selectedElementData,
    dispatch,
    canManageLibrary,
}) => {
    const [renamingContent, setRenamingContent] = useState<boolean>(false);
    const [contentName, setContentName] = useState(content.displayName);
    const [oldContentName, setOldContentName] = useState(content.displayName); // To keep track of previously set display name in case user hit the x button.
    const dateUploaded = new Date(content.dateUploaded);
    const formattedDate = moment(dateUploaded).format('D MMM YYYY, h:mm A');
    const editFileNameInput = useRef<HTMLInputElement>(null);

    useEffect(() => {
        setContentName(content.displayName);
    }, [content]);

    useEffect(() => {
        if (editFileNameInput && editFileNameInput.current) {
            editFileNameInput.current.focus();
        }
    }, [renamingContent, editFileNameInput]);

    const renameFile = async (contentID: number, newContentName: string) => {
        try {
            await request.put(configs.contentLibraryEndpointRename, { contentID, displayName: newContentName });
            dispatch(contentLibraryAction.updateContentName({ contentID, displayName: newContentName }));
        } catch (err) {
            console.error(err);
            setContentName(oldContentName);
            eventService.notify(err.customMessage ? err.customMessage : ERR_RENAME_FILE, true);
        }
    };

    const onEditNameClick = async (e: MouseEvent | KeyboardEvent) => {
        e.stopPropagation();
        e.preventDefault();
        if (contentName.length > MAX_FILE_NAME_CHAR) {
            eventService.notify(ERR_FILE_NAME_TOO_LONG, true);
            return;
        }
        await renameFile(content.contentID, contentName);
        setRenamingContent(false);
        setOldContentName(contentName);
    };

    const onCloseEditNameClick = (e: MouseEvent | KeyboardEvent) => {
        e.stopPropagation();
        setContentName(oldContentName);
        setRenamingContent(false);
    };

    return (
        <Styled.ImagePreviewColumn>
            <Styled.ImagePreview src={content.thumbnailURL} alt="Preview on the content library" />
            {renamingContent ? (
                <Styled.RenamingContentIcons>
                    <Styled.FileNameInput
                        value={contentName}
                        onClick={(e: MouseEvent<HTMLInputElement>) => {
                            e.stopPropagation();
                        }}
                        onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => handleKeyPress(e, () => e.stopPropagation())}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            setContentName(e.target.value);
                        }}
                        ref={editFileNameInput}
                    />
                    <Styled.IconDiv>
                        <div data-test-id="edit-name-icon-check">
                            <Styled.IconsDiv disable={contentName.length < 3 || /^\s+$/.test(contentName)}>
                                <Styled.Icon
                                    tabIndex={0}
                                    onClick={async (e: MouseEvent<SVGElement>) => onEditNameClick(e)}
                                    onKeyDown={async (e) =>
                                        handleKeyPress(e as unknown as KeyboardEvent<HTMLElement>, async () =>
                                            onEditNameClick(e)
                                        )
                                    }
                                    icon="check"
                                    aria-label="enter"
                                />
                            </Styled.IconsDiv>
                        </div>
                        <div data-test-id="edit-name-icon-cancel">
                            <Styled.Icon
                                tabIndex={0}
                                icon="times"
                                aria-label="close edit mode"
                                color="true"
                                onClick={(e: MouseEvent<SVGElement>) => onCloseEditNameClick(e)}
                                onKeyDown={async (e) =>
                                    handleKeyPress(e as unknown as KeyboardEvent<HTMLElement>, async () =>
                                        onCloseEditNameClick(e)
                                    )
                                }
                            />
                        </div>
                    </Styled.IconDiv>
                </Styled.RenamingContentIcons>
            ) : (
                <Styled.ImagePreviewName
                    canManageLibrary={canManageLibrary}
                    data-test-id="edit-name-icon"
                    onClick={canManageLibrary ? () => setRenamingContent(true) : () => {}}
                    onKeyDown={(e: KeyboardEvent<HTMLDivElement>) =>
                        canManageLibrary && handleKeyPress(e, () => setRenamingContent(true))
                    }
                    role="button"
                    aria-label="Edit image name"
                    tabIndex={0}
                >
                    <Styled.FileNameDiv data-tip data-for="fileName">
                        {contentName.length > MAX_FILE_CHAR_DISPLAY && (
                            <ReactTooltip id="fileName" place="bottom" effect="solid">
                                <span style={{ fontWeight: 'bold' }}>{contentName}</span>
                            </ReactTooltip>
                        )}
                        <p>
                            {contentName.length > MAX_FILE_CHAR_DISPLAY
                                ? `${contentName.substring(0, MAX_FILE_CHAR_DISPLAY)}...`
                                : contentName}
                        </p>
                    </Styled.FileNameDiv>
                    <Styled.RenameContentDiv>
                        <Styled.Icon id="edit-name" color="true" icon="pencil" aria-label="rename" />
                    </Styled.RenameContentDiv>
                </Styled.ImagePreviewName>
            )}

            <Styled.ImagePreviewInfo>
                <Styled.Icon icon="user" />
                {`${content.ownerName} ${content.ownerLastName}`}
            </Styled.ImagePreviewInfo>
            <Styled.ImagePreviewInfo>
                <Styled.Icon icon="calendar-day" />
                {formattedDate}
            </Styled.ImagePreviewInfo>
            <Styled.UploadToPikassoButton>
                <BananatagButton
                    type="solid"
                    size="large"
                    text="Select"
                    ariaLabel="Select image to canvas"
                    styling={{ margin: 4, padding: '0 72px', fontSize: 13, height: 32 }}
                    onClick={async () => {
                        dispatch(contentLibraryAction.setLoading(true));
                        await uploadImageToBackend(
                            content.contentURL,
                            selectedElementData,
                            content.contentExtension,
                            dispatch,
                            true
                        );
                        dispatch(contentLibraryAction.setLoading(false));
                        hideImageLibrary();
                    }}
                />
            </Styled.UploadToPikassoButton>
        </Styled.ImagePreviewColumn>
    );
};

export default connect()(ImagePreviewColumn);
